import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import useApi from "./useApi";

// Fetch all vendors with pagination
export default function useVendor(page = 1, perPage = 20, search = '', status = 'all') {
  const { get } = useApi();
  const params = new URLSearchParams({
    page: page,
    per_page: perPage
  });
  
  if (search) {
    params.append('search', search);
  }
  
  if (status && status !== 'all') {
    params.append('status', status);
  }
  
  const url = `admin/vendors?${params.toString()}`;
  return get(url);
}

// Fetch vendor by ID
export function useVendorById(id) {
  const { getById } = useApi();
  return getById("admin/vendors", id);
}

// Add vendor (supports FormData)
export function useAddVendor({ onSuccess, onError, onSettled } = {}) {
  const { post } = useApi();
  // Note: endpoint is 'vendor/register' (not admin)
  return post("vendor/register", { onSuccess, onError, onSettled });
}

// Update vendor
export function useUpdateVendor({ onSuccess, onError, onSettled } = {}) {
  const { put } = useApi();
  // Usage: mutate({ id, payload })
  return put("admin/vendors/", { onSuccess, onError, onSettled });
}

// Delete vendor
export function useDeleteVendor({ onSuccess, onError, onSettled } = {}) {
  const { del } = useApi();
  // Usage: mutate(id)
  return del("admin/vendors", { onSuccess, onError, onSettled });
}

// Upload vendor profile image
export function useUploadVendorProfileImage({ onSuccess, onError, onSettled, isVendor = true } = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, file }) => {
      const formData = new FormData();
      formData.append("profile_image", file);
      const api = axios.create({
        baseURL: import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : 'http://localhost:8000/api',
        withCredentials: true,
      });
      let response;
      if (!isVendor && id) {
        // Admin/employee uploads for specific vendor
        response = await api.post(`admin/vendors/${id}/profile-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } else {
        // Vendor uploads own profile image
        response = await api.post(`vendor/profile-image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return response.data;
    },
    onSuccess: (data, variables, context) => {
      if (typeof onSuccess === "function") onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      if (typeof onError === "function") onError(error, variables, context);
    },
    onSettled: (data, error, variables, context) => {
      if (typeof onSettled === "function") onSettled(data, error, variables, context);
    },
  });
}

// Fetch vendor profile (by ID)
export function useVendorProfile(id) {
  const { getById } = useApi();
  return getById("admin/vendors", id);
}

// Fetch all categories
export function useVendorCategories() {
  const { get } = useApi();
  return get("public/categories");
}

// Fetch current vendor's full details
export function useMyVendor() {
  const { get } = useApi();
  return get('vendor/me');
}

// Update current vendor's own profile
export function useUpdateMyVendor({ onSuccess, onError, onSettled } = {}) {
  const { put } = useApi();
  // Usage: mutate({ payload })
  return put("vendor/profile", { onSuccess, onError, onSettled });
}
